import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Paperclip, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cbLibroUpload, cbLibroAllegato } from "@/lib/api";

export const libroError = async (error, fallback) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try { return JSON.parse(await data.text()).detail || fallback; } catch { return fallback; }
    }
    return typeof data?.detail === "string" ? data.detail : fallback;
};

const ReceiptPreview = ({ token, rowId, attachment, index }) => {
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        let active = true, url;
        cbLibroAllegato(token, rowId, index).then(blob => {
            if (!active) return;
            url = URL.createObjectURL(blob);
            setPreview({ url, type: blob.type });
        }).catch(async e => { const message = await libroError(e, "Allegato non disponibile"); if (active) setError(message); });
        return () => { active = false; if (url) URL.revokeObjectURL(url); };
    }, [token, rowId, index]);
    return <div className="border border-[#9AA2AD]/20 p-3 min-w-0" data-testid={`receipt-${index}`}>
        <div className="text-xs break-words mb-2" data-testid={`receipt-name-${index}`}>{attachment.filename}</div>
        {error ? <p className="text-xs text-red-400" role="alert" data-testid={`receipt-error-${index}`}>{error}</p> : !preview ? <p className="text-xs text-[#9AA2AD]" data-testid={`receipt-loading-${index}`}>Caricamento…</p> : <>
            {preview.type.includes("pdf") ? <iframe title={attachment.filename} src={preview.url} className="w-full aspect-[3/4] bg-white" data-testid={`receipt-preview-${index}`} />
                : <img src={preview.url} alt={attachment.filename} className="w-full aspect-[4/3] object-contain bg-[#0B0D10]" data-testid={`receipt-preview-${index}`} />}
            <a href={preview.url} target="_blank" rel="noreferrer" data-testid={`receipt-open-${index}`} className="inline-flex items-center gap-2 text-xs text-[#4B7BEA] hover:underline mt-3"><ExternalLink size={14} />Apri originale</a>
        </>}
    </div>;
};

export const LibroCassaAttachments = ({ token, row, onClose, onSaved }) => {
    const [attachments, setAttachments] = useState(row.allegati);
    const [busy, setBusy] = useState(false);
    const upload = async files => {
        if (!files?.length) return;
        setBusy(true);
        try {
            const added = await cbLibroUpload(token, row.id, files);
            setAttachments(prev => [...prev, ...added]);
            toast.success(`${added.length} allegati salvati`);
            onSaved();
        } catch (e) { toast.error(await libroError(e, "Caricamento non riuscito")); }
        finally { setBusy(false); }
    };
    return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
        <DialogContent className="bg-[#15181C] text-[#F4F2ED] border-[#9AA2AD]/30 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="libro-attachments-dialog">
            <DialogHeader><DialogTitle>Allegati ({attachments.length})</DialogTitle><DialogDescription className="break-words">{row.descrizione}</DialogDescription></DialogHeader>
            <label className="inline-flex items-center gap-2 text-sm" htmlFor="libro-receipts"><Paperclip size={16} />Carica foto o PDF</label>
            <input id="libro-receipts" data-testid="libro-receipts-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/tiff,image/bmp,application/pdf" multiple disabled={busy} onChange={e => { upload(e.target.files); e.target.value = ""; }} className="w-full min-w-0 text-xs file:bg-[#252A32] file:text-white file:border-0 file:p-2" />
            {busy && <p data-testid="libro-upload-status" className="flex gap-2 text-sm"><Loader2 className="animate-spin" size={16} />Salvataggio…</p>}
            <div className="grid sm:grid-cols-2 gap-3" data-testid="libro-receipts-list">{attachments.map((a, i) => <ReceiptPreview key={a.file_id || `${a.secure_url}-${i}`} token={token} rowId={row.id} attachment={a} index={i} />)}</div>
            {!attachments.length && <p className="text-sm text-[#9AA2AD]" data-testid="libro-no-attachments">Nessun allegato.</p>}
            <Button variant="outline" disabled={busy} onClick={onClose} data-testid="libro-attachments-close">Chiudi</Button>
        </DialogContent>
    </Dialog>;
};